import { useState, useEffect } from "react";
import InputDiv from "./InputDiv";
import Button from "./Button";
import AddressField from "./AddressField";
import { Form, Formik } from "formik";
import FieldGrid from "./FieldGrid";
import FieldGridItem from "./FieldGridItem";
import * as Yup from "yup";
import { useMutation, useQueryCache } from "react-query";
import { useCookies } from "react-cookie";
import Loader from "./Loader";
import forEach from "lodash/forEach";
import FormContainer from "./FormContainer";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { usePlace, useUser } from "../api/queries";
import { updatePlace, createPlace } from "../api/mutations";
import ENV from "../constants/envConstants";
import { cookieExpiry } from "../constants/cookieConstants";
import { identifyGroupSegment, trackEventSegment } from "../utils/segmentUtils";
import { Helmet } from "react-helmet";
import PhoneInputField from "./PhoneInputField";
import { isValidPhoneNumber } from "react-phone-number-input";
import SwitchInput from "./SwitchInput";

function BusinessPage(props) {
  const [cookies, setCookie, removeCookie] = useCookies(["placeId"]);

  const [placeId, setPlaceId] = useState(cookies.placeId);
  const [placeUpdateOrCreate] = useMutation(cookies.placeId ? updatePlace : createPlace);
  const { data: user } = useUser();

  const { status, data } = usePlace(placeId);

  const [loaded, setLoaded] = useState(false);

  let cache = useQueryCache();

  const [formData, setFormData] = useState({
    businessName: "",
    address: "",
    addressPoint: "",
    businessEmailAddress: "",
    businessPhoneNumber: "",
    slug: "",
    enableGuestRegistration: false,
  });

  let navigate = useNavigate();

  useEffect(() => {
    if (user && !placeId) {
      if (user.managed_places.length > 0) {
        if (user.managed_places.length > 1) {
          window.location.href = ENV.DASHBOARD_URL;
        } else {
          setPlaceId(user.managed_places[0].uuid);
          setCookie("placeId", user.managed_places[0].uuid, {
            path: "/",
            expires: cookieExpiry(),
            sameSite: "strict",
            secure: ENV.IS_PRODUCTION ? true : false,
          });
        }
      } else {
        setLoaded(true);
      }
    }
  }, [user]);

  useEffect(() => {
    if (loaded === false && data) {
      const loadedData = {
        businessName: data.name,
        address: data.address,
        addressPoint: {
          lat: data.point.latitude,
          lng: data.point.longitude,
        },
        businessEmailAddress: data.owner_email,
        businessPhoneNumber: data.phone_number,
        slug: data.slug,
        enableGuestRegistration: data.contact_tracing_enabled,
      };
      setFormData(loadedData);
      setLoaded(true);
    }
  }, [data, loaded]);

  useEffect(() => {
    props.setHeader("Business profile");
    props.setSubheader("Complete your business information");
    return () => {
      props.setHeader("");
      props.setSubheader("");
    };
  }, [props]);

  const schema = Yup.object({
    businessName: Yup.string().required("This field is required."),
    address: Yup.string().required("This field is required."),
    addressPoint: Yup.string()
      .transform((_, input) =>
        input != null && input.toString ? input.toString() : "Please select an address using the google maps field."
      )
      .required("Please select an address using the google maps field."),
    businessEmailAddress: Yup.string().required("This field is required.").email("Please enter a valid email."),
    businessPhoneNumber: Yup.string()
      .required("This field is required.")
      .test(
        "is-valid-number",
        // eslint-disable-next-line no-template-curly-in-string
        "${value} is not a valid phone number.",
        async (value) => {
          if (value) {
            return isValidPhoneNumber(value);
          }
          return false;
        }
      ),
    slug: Yup.string().required("This field is required.").lowercase("URL must be lowercase.").strict(),
  });

  const djTojsDict = {
    name: "business_name",
    owner_email: "businessEmailAddress",
    phone_number: "businessPhoneNumber",
    slug: "slug",
    contact_tracing_enabled: "enableGuestRegistration",
  };

  const onSubmit = async (values, bag) => {
    let reqBody = {
      name: values.businessName,
      address: values.address,
      phone_number: values.businessPhoneNumber,
      owner_email: values.businessEmailAddress,
      slug: values.slug,
      contact_tracing_enabled: values.enableGuestRegistration,
      point: {
        latitude: values.addressPoint.lat,
        longitude: values.addressPoint.lng,
      },
    };
    await placeUpdateOrCreate(
      { values: reqBody, placeId: cookies.placeId },
      {
        onSuccess: (data) => {
          if (!cookies.placeId) {
            setCookie("placeId", data.data.uuid, {
              path: "/",
              expires: cookieExpiry(),
              sameSite: "Lax",
              secure: ENV.IS_PRODUCTION ? true : false,
            });
          }
          identifyGroupSegment(data.data.uuid, {
            name: values.businessName,
            phone: values.businessPhoneNumber,
            email: values.businessEmailAddress,
          });
          trackEventSegment(`${cookies.placeId ? "Updated" : "Created"} place`);
          navigate("/menu-builder/pdf-initial");
        },
        onError: (error, variables) => {
          const data = error.response.data;
          switch (error.response.status) {
            case 400:
              if (data.error_type === "ValidationError") {
                forEach(data.errors, (error) => {
                  bag.setFieldError(djTojsDict[error.field], error.message);
                });
              }
              break;
            case 401:
              removeCookie("placeId");
              break;
            case 500:
              Swal.fire({
                title: "Oops!",
                text:
                  "It's not your fault! Our server has encountered an internal error. An adminstrator has been notified. Try refreshing your page in the interim.",
                width: "35rem",
              });
              break;
            default:
              Swal.fire({
                title: "Oops!",
                text: "An unknown error has occured. Try refreshing your page in the interim.",
                width: "35rem",
              });
          }
        },
      }
    );
    bag.setSubmitting(false);
  };

  if (loaded === false || !user) {
    return <Loader />;
  }

  return (
    <FormContainer>
      <Helmet>
        <title>Eatsee - Business profile</title>
      </Helmet>
      <Formik validationSchema={schema} initialValues={formData} onSubmit={onSubmit}>
        {(formik) =>
          formik.isSubmitting ? (
            <Loader type={"beat"} size={20} />
          ) : (
            <Form>
              <InputDiv text="Business name" placeholder="Add business name" name="businessName" hasLabel={true} />
              <AddressField />
              <FieldGrid>
                <FieldGridItem>
                  <InputDiv
                    text="Business email address"
                    placeholder={"Enter email address"}
                    name="businessEmailAddress"
                    hasLabel={true}
                  />
                </FieldGridItem>
                <FieldGridItem>
                  <PhoneInputField
                    text="Business phone number"
                    placeholder="Enter phone number"
                    name="businessPhoneNumber"
                    hasLabel={true}
                  />
                </FieldGridItem>
              </FieldGrid>
              <InputDiv
                text="Create digital menu URL"
                placeholder="For example thediner"
                name="slug"
                inputPrefix="eatsee.menu/"
                helpText={
                  "Your digital menu will be hosted on an eatsee.menu link such as eatsee.menu/thediner. A unique URL is created for each restaurant. If your restaurant name is taken, please try something else."
                }
                hasLabel={true}
              />
              <SwitchInput
                defaultChecked={formik.values.enableGuestRegistration}
                toggleChange={(value) =>
                  formik.setFieldValue("enableGuestRegistration", !formik.values.enableGuestRegistration)
                }
                label={"Enable digital guest register"}
                helpText={
                  "In line with COVID-19 regulations, it is mandatory (in Australia) to enable contact tracing at your venue. Customers can check in \
                  through a QR code guest register before dining in. This information is stored securely in your restaurant dashboard and not \
                  used for marketing purposes."
                }
              />
              <div style={{ marginBottom: "3em" }}></div>
              <Button type="submit" text="Next step" hasMargin={false} />
            </Form>
          )
        }
      </Formik>
    </FormContainer>
  );
}

export default BusinessPage;
